/**
 * The sentence to show a person for a failed request.
 *
 * The API answers with RFC 9457 problem documents, and Eden wraps them in an
 * error whose `message` is the document serialized as JSON — accurate, and not
 * something to put in front of a volunteer. The `detail` is the sentence the
 * server wrote for a human ("They have no background check on file…"), so that
 * is what every screen shows, on both platforms.
 */
export function describeError(error: unknown): string {
  const value = (error as { value?: unknown } | null)?.value
  if (typeof value === 'object' && value !== null) {
    const problem = value as { detail?: unknown; title?: unknown; summary?: unknown }
    if (typeof problem.detail === 'string' && problem.detail) return problem.detail
    // Framework validation errors carry a summary rather than a detail.
    if (typeof problem.summary === 'string' && problem.summary) return problem.summary
    if (typeof problem.title === 'string' && problem.title) return problem.title
  }
  if (typeof value === 'string' && value) return value
  if (error instanceof Error && error.message && !error.message.startsWith('{')) {
    return error.message
  }
  return 'Something went wrong. Please try again.'
}

/**
 * Did the request fail to reach the server at all (offline, DNS, refused)?
 *
 * Eden reports a failed `fetch` as an error with **status 503** whose value is
 * the underlying TypeError — indistinguishable by status from a real 503 the
 * server sent. The value tells them apart: the API's own errors are problem
 * documents (`type`, `title`); a transport failure is an Error object.
 */
export function isTransportFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const status = (error as { status?: unknown }).status
  // No status: a failed fetch (a TypeError: "Failed to fetch", "Load failed",
  // "NetworkError…"), not any error a hook threw.
  if (status === undefined)
    return (
      error instanceof TypeError ||
      /failed to fetch|load failed|network ?error/i.test(String((error as Error).message))
    )
  const value = (error as { value?: unknown }).value
  return (
    status === 503 &&
    (value instanceof Error ||
      (typeof value === 'object' && value !== null && !('type' in value) && !('title' in value)))
  )
}

/** What kind of failure this is, which decides what a person can do about it. */
export type FailureKind =
  /** The request never reached the server (offline, dropped, refused). */
  | 'offline'
  /** Their role does not allow it. */
  | 'forbidden'
  /** It is gone, or was never theirs to see. */
  | 'missing'
  /** Someone or something changed it first (409). */
  | 'conflict'
  /** The server refused what was sent (400, 422): the detail says why. */
  | 'invalid'
  /** The server failed (5xx). */
  | 'server'
  | 'unknown'

export interface Failure {
  kind: FailureKind
  /** What broke, in a few words: the heading. */
  title: string
  /** Why, in the server's words when it gave some. */
  detail: string
  /** What is still safe: nothing half-done, nothing lost. */
  safe: string
  /** Whether trying the same thing again can work. */
  retryable: boolean
}

const statusOf = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null)?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * A failed request, explained: what broke, why, what is still safe, and
 * whether to try again. Every error a screen shows goes through here, so a
 * dropped connection never reads like a refusal, and a refusal never offers
 * a retry that cannot work.
 *
 * The "safe" line rests on how the API works: every write is one transaction,
 * so a write that failed changed nothing; and a form keeps what was typed.
 */
export function classifyError(error: unknown): Failure {
  const detail = describeError(error)
  if (isTransportFailure(error))
    return {
      kind: 'offline',
      title: 'Couldn’t reach the server',
      detail:
        (globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine === false
          ? 'This device is offline.'
          : 'The connection dropped before the server answered.',
      safe: 'Nothing was changed, and what you entered is still here.',
      retryable: true,
    }
  const status = statusOf(error)
  if (status === 403)
    return {
      kind: 'forbidden',
      title: 'Your role can’t do this',
      detail,
      safe: 'Nothing was changed. An admin can give your role this permission.',
      retryable: false,
    }
  if (status === 404)
    return {
      kind: 'missing',
      title: 'It isn’t there any more',
      detail: detail === 'Not found' ? 'It may have been removed by someone else.' : detail,
      safe: 'Nothing else was affected.',
      retryable: false,
    }
  if (status === 409)
    return {
      kind: 'conflict',
      title: 'Something changed first',
      detail,
      safe: 'Nothing was changed. Refresh to see the latest, then try again.',
      retryable: true,
    }
  if (status === 400 || status === 422)
    return {
      kind: 'invalid',
      title: 'That can’t be saved as it is',
      detail,
      safe: 'Nothing was changed. Fix it and save again.',
      retryable: false,
    }
  if (status !== undefined && status >= 500)
    return {
      kind: 'server',
      title: 'The server had a problem',
      detail:
        detail === 'Something went wrong. Please try again.'
          ? 'It failed on our side, not yours.'
          : detail,
      safe: 'Nothing was changed. Trying again usually works.',
      retryable: true,
    }
  return {
    kind: 'unknown',
    title: 'That didn’t work',
    detail,
    safe: 'Nothing was changed.',
    retryable: true,
  }
}
